<script setup lang="ts">
import { computed, onActivated, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useDisplay } from "vuetify";
import { useAbility } from "@casl/vue";
import { storeToRefs } from "pinia";
import { usePracticeStaffMe } from "@/composables/models/practice/usePracticeStaff";
import { useAuthStore } from "@/stores/authStore";
import { useEnvironmentStore, EEnvironmentType } from "@/stores/environmentStore";
import AppTabs, { type TAppTab } from "@/components/shared/tabs/AppTabs.vue";
import ProfilePictureCard from "@/components/models/settings/ProfilePictureCard.vue";
import PersonalInformationCard from "@/components/models/settings/PersonalInformationCard.vue";
import ApplicationPreferencesCard from "@/components/models/settings/ApplicationPreferencesCard.vue";
import ChatPreferencesCard from "@/components/models/settings/ChatPreferencesCard.vue";
import LegalCard from "@/components/models/settings/LegalCard.vue";
import SecurityCard from "@/components/models/settings/SecurityCard.vue";
import DangerZoneCard from "@/components/models/settings/DangerZoneCard.vue";
import UserBadgesCard from "@/components/models/settings/UserBadgesCard.vue";
import UserPermissionsCard from "@/components/models/settings/UserPermissionsCard.vue";
import PracticeStaffApiKeys from "@/components/models/practice/practiceStaff/PracticeStaffApiKeys.vue";

definePage({
  meta: {
    title: "generic.entity.settings",
  },
});

type TTabValue = "profile" | "integrations" | "preferences" | "security" | "permissions";

const ROUTE_NAME = "/settings/[[tab]]";
const DEFAULT_TAB: TTabValue = "profile";

const { t } = useI18n();
const { smAndDown } = useDisplay();
const { can } = useAbility();
const route = useRoute();
const router = useRouter();

const { data: practiceStaffMe } = usePracticeStaffMe();

// `/practice-staff/me` (above) is a lean profile-editing serializer and does not reliably
// carry `groups`/`permissions`. `loggedAsStaff` comes from the auth session payload that
// builds the CASL ability, so it is the authoritative source for the user's own access.
const { loggedAsStaff } = storeToRefs(useAuthStore());
const { currentEnv } = useEnvironmentStore();

const activeTab = ref<TTabValue>(DEFAULT_TAB);

const canReadApiKeys = computed(() =>
  can($permActions.READ, $permSubjects.PRACTICE__PRACTICESTAFFAPIKEYSTORAGE)
);

// Still under evaluation — only shown on DEV, MR, DEVEL, and QA builds, same
// convention as other environment-gated features (see EEnvironmentType usages).
const PERMISSIONS_TAB_ENVIRONMENTS = new Set<string>([
  EEnvironmentType.DEV,
  EEnvironmentType.MR,
  EEnvironmentType.DEVEL,
  EEnvironmentType.QA,
]);
const canShowPermissions = computed(() => PERMISSIONS_TAB_ENVIRONMENTS.has(currentEnv));

const tabs = computed<TAppTab[]>(() => [
  {
    value: "profile",
    title: t("generic.entity.profile"),
    icon: "mdi-account-circle-outline",
  },
  ...(canReadApiKeys.value
    ? [
        {
          value: "integrations",
          title: t("features.integrations.title"),
          icon: "mdi-puzzle-outline",
        },
      ]
    : []),
  {
    value: "preferences",
    title: t("generic.entity.preferences"),
    icon: "mdi-tune-variant",
  },
  {
    value: "security",
    title: t("models.auth.users.accountActions.security.title"),
    icon: "mdi-shield-lock-outline",
  },
  ...(canShowPermissions.value
    ? [
        {
          value: "permissions",
          title: t("generic.entity.permissions"),
          icon: "mdi-shield-key-outline",
          badge: t("generic.badges.dev"),
        },
      ]
    : []),
]);

const isVisibleTab = (value?: string): value is TTabValue =>
  tabs.value.some((tab) => tab.value === value);

const routeTab = computed(() => (route.params as { tab?: string }).tab);

const handleTabPath = () => {
  if (isVisibleTab(routeTab.value)) {
    activeTab.value = routeTab.value;
    return;
  }

  router.replace({
    name: ROUTE_NAME,
    params: { ...route.params, tab: DEFAULT_TAB },
    query: route.query,
  });
};

const handleTabChange = (value: string) => {
  if (value === activeTab.value) return;

  router.push({
    name: ROUTE_NAME,
    params: { ...route.params, tab: value },
    query: route.query,
  });
};

watch(routeTab, () => handleTabPath());
watch(tabs, () => handleTabPath());

onMounted(() => handleTabPath());
onActivated(() => handleTabPath());
</script>

<template>
  <div class="tw:h-full tw:w-full tw:overflow-y-auto tw:bg-background">
    <div
      :class="[
        'tw:mx-auto tw:flex tw:w-full tw:max-w-[1346px] tw:flex-col tw:gap-6',
        smAndDown ? 'tw:!p-4' : 'tw:!px-4 tw:!py-6',
      ]"
    >
      <h2 class="tw:text-3xl tw:font-bold tw:text-primary tw:md:text-4xl">
        {{ t("generic.entity.settings") }}
      </h2>

      <app-tabs
        :items="tabs"
        :model-value="activeTab"
        testid="settings-tabs"
        @update:model-value="handleTabChange"
      />

      <div class="tw:flex tw:flex-col tw:gap-5">
        <template v-if="activeTab === 'profile'">
          <profile-picture-card
            v-if="practiceStaffMe"
            :staff="practiceStaffMe"
          />

          <personal-information-card
            v-if="practiceStaffMe"
            :staff="practiceStaffMe"
          />

          <v-skeleton-loader
            v-if="!practiceStaffMe"
            type="article"
          />
        </template>

        <template v-else-if="activeTab === 'integrations'">
          <practice-staff-api-keys
            v-if="practiceStaffMe"
            :staff="practiceStaffMe"
          />

          <v-skeleton-loader
            v-else
            type="article"
          />
        </template>

        <template v-else-if="activeTab === 'preferences'">
          <application-preferences-card />
          <chat-preferences-card />
        </template>

        <template v-else-if="activeTab === 'security'">
          <legal-card />
          <security-card />
          <danger-zone-card />
        </template>

        <template v-else-if="activeTab === 'permissions'">
          <v-alert
            type="warning"
            variant="tonal"
            density="comfortable"
          >
            {{ t("models.settings.permissions.myAccess.developersOnlyNotice") }}
          </v-alert>

          <template v-if="loggedAsStaff">
            <user-badges-card :staff="loggedAsStaff" />
            <user-permissions-card :staff="loggedAsStaff" />
          </template>

          <v-skeleton-loader
            v-if="!loggedAsStaff"
            type="article"
          />
        </template>
      </div>
    </div>
  </div>
</template>
